import React, { useRef, useEffect } from 'react';
import moment from 'moment';
import { isAllLocations, getLocationName, getLocationBadgeColor } from '../../../util/locationUtils';
import { getTechnicianColor, getStageColor } from '../../../util/colorCodingUtils';
import TechBadge from '../../common/TechBadge';

const CalendarPopover = ({ job, position, onClose, onViewDetails, locations, selectedLocationId, colorMode, stageColors, technicians }) => {
    const popoverRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (popoverRef.current && !popoverRef.current.contains(e.target)) {
                onClose();
            }
        };
        const handleEscape = (e) => {
            if (e.key === 'Escape') onClose();
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [onClose]);

    // Adjust position to stay within viewport
    useEffect(() => {
        if (!popoverRef.current) return;
        const rect = popoverRef.current.getBoundingClientRect();
        const el = popoverRef.current;

        if (rect.right > window.innerWidth - 16) {
            el.style.left = 'auto';
            el.style.right = '16px';
        }
        if (rect.bottom > window.innerHeight - 16) {
            el.style.top = `${position.top - rect.height - 8}px`;
        }
    }, [position]);

    if (!job) return null;

    const isDealer = job.requestType === 'dealer';
    const defaultAccent = isDealer ? '#FF9516' : '#2E56F6';
    let jobColor = null;
    if (colorMode === 'stage') {
        jobColor = getStageColor(job.currentState || '', stageColors);
    } else if (colorMode === 'technician') {
        const techKey = job.assigned_technician_id || job.assignedTechnicianId || job.assignedTechnicianName;
        if (techKey) {
            jobColor = getTechnicianColor(techKey, technicians);
        } else {
            jobColor = { wash: '#F3F4F6', accent: '#9CA3AF' };
        }
    }
    const accentColor = jobColor ? jobColor.accent : defaultAccent;
    const showLocationBadge = isAllLocations(selectedLocationId) && Array.isArray(locations) && locations.length > 1;
    const multiDayInfo = job.isMultiDay ? `Day ${job.currentDay} of ${job.totalDays}` : null;

    // Build vehicle string from individual fields
    const vehicle = [job.vehicleYear, job.vehicleMake, job.vehicleModel, job.vehicleTrim]
        .filter(Boolean)
        .join(' ') || 'Vehicle info unavailable';

    const service = [job.category, job.serviceDescription].filter(Boolean).join(' - ') || 'Service';

    return (
        <div
            ref={popoverRef}
            className="cal-popover"
            style={{
                position: 'fixed',
                top: position.top,
                left: position.left,
                zIndex: 9999,
            }}
        >
            {/* Accent bar */}
            <div className="cal-popover-accent" style={{ background: accentColor }} />

            {/* Header */}
            <div className="cal-popover-header">
                <span className="cal-popover-type-badge" style={{
                    background: accentColor + '18',
                    color: accentColor,
                }}>
                    {isDealer ? 'Dealer' : 'Direct'}
                </span>
                <button className="cal-popover-close" onClick={onClose}>&times;</button>
            </div>

            {/* Content */}
            <div className="cal-popover-body">
                <div className="cal-popover-vehicle">{vehicle}</div>
                <div className="cal-popover-service">{service}</div>
                <div className="cal-popover-customer">{job.customerName || job.dealerName || 'Customer'}</div>

                <div className="cal-popover-details">
                    <div className="cal-popover-detail-row">
                        <span className="cal-popover-detail-label">Date</span>
                        <span>{moment(job.startDate).format('ddd, MMM D, YYYY')}{multiDayInfo ? ` (${multiDayInfo})` : ''}</span>
                    </div>
                    {job.currentState && (
                        <div className="cal-popover-detail-row">
                            <span className="cal-popover-detail-label">Status</span>
                            <span style={{ textTransform: 'capitalize' }}>{job.currentState}</span>
                        </div>
                    )}
                    {(() => {
                        const ta = job.tech_assignment;
                        const isMulti = ta && (ta.effective_state === 'mixed' || ta.effective_state === 'partial');
                        if (isMulti && Array.isArray(ta.techs) && ta.techs.length > 0) {
                            return (
                                <div className="cal-popover-detail-row">
                                    <span className="cal-popover-detail-label">Technicians</span>
                                    <span className="d-flex flex-wrap" style={{ gap: '6px' }}>
                                        {ta.techs.map(t => (
                                            <TechBadge
                                                key={t.technician_id || t.user_id || t.name}
                                                name={t.name}
                                                color={t.user_color}
                                            />
                                        ))}
                                    </span>
                                </div>
                            );
                        }
                        if (job.assignedTechnicianName) {
                            return (
                                <div className="cal-popover-detail-row">
                                    <span className="cal-popover-detail-label">Technician</span>
                                    <span className="cal-popover-tech-badge">{job.assignedTechnicianName}</span>
                                </div>
                            );
                        }
                        return null;
                    })()}
                    {showLocationBadge && (job.location_id || job.locationId) && (
                        <div className="cal-popover-detail-row">
                            <span className="cal-popover-detail-label">Location</span>
                            <span className="cal-popover-loc-badge" style={{
                                backgroundColor: getLocationBadgeColor(job.location_id || job.locationId, locations),
                            }}>
                                {job.locationName || job.location_name || getLocationName((locations || []).find(l => String(l.location_id) === String(job.location_id || job.locationId)))}
                            </span>
                        </div>
                    )}
                    <div className="cal-popover-detail-row">
                        <span className="cal-popover-detail-label">Request</span>
                        <span>#{job.requestId}</span>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="cal-popover-footer">
                <button
                    className="cal-popover-view-btn"
                    onClick={() => {
                        onViewDetails(job.requestId);
                        onClose();
                    }}
                >
                    View Full Details &rarr;
                </button>
            </div>
        </div>
    );
};

export default CalendarPopover;
